"use client";

import { FaFacebookF, FaLinkedinIn, FaWhatsapp, FaXTwitter } from "react-icons/fa6";
import { Link2, Check } from "lucide-react";
import { useState } from "react";

type PostShareBarProps = {
  url: string;
  title: string;
};

export default function PostShareBar({ url, title }: PostShareBarProps) {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const items = [
    {
      label: "X",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: FaXTwitter,
      className: "bg-foreground text-background hover:opacity-90",
    },
    {
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: FaFacebookF,
      className: "bg-[#1877f2] text-white hover:opacity-90",
    },
    {
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      icon: FaWhatsapp,
      className: "bg-[#25d366] text-white hover:opacity-90",
    },
    {
      label: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: FaLinkedinIn,
      className: "bg-[#0a66c2] text-white hover:opacity-90",
    },
  ];

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <p className="mb-3 text-sm font-semibold">Share this post</p>
      <div className="flex flex-wrap gap-2">
        {items.map(({ label, href, icon: Icon, className }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share on ${label}`}
            className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-opacity ${className}`}
          >
            <Icon size={14} />
            {label}
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
          aria-label="Copy link"
        >
          {copied ? <Check size={14} /> : <Link2 size={14} />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
